import { Component, Suspense, useEffect, useId, useRef, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';

export function PageLoading() {
  const { t } = useTranslation();
  return <section role="status" aria-label={t('pageLoad.loading')} aria-busy="true" className="mx-auto flex min-h-48 max-w-xl items-center justify-center gap-3 p-6 text-foreground">
    <Spinner aria-hidden="true" className="h-5 w-5 shrink-0 motion-reduce:animate-none" />
    <p className="text-sm font-medium">{t('pageLoad.loading')}</p>
  </section>;
}

export function PageLoadFailure({ onReload }: { onReload: () => void }) {
  const { t } = useTranslation();
  const titleId = useId();
  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => { title.current?.focus(); }, []);
  return <div className="px-4 py-6"><section role="alert" aria-labelledby={titleId} className="mx-auto max-w-xl space-y-4 rounded-lg border border-border bg-card p-6 text-foreground">
    <AlertTriangle aria-hidden="true" className="h-6 w-6 text-warning" />
    <h1 id={titleId} ref={title} tabIndex={-1} className="text-2xl font-semibold tracking-tight focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">{t('pageLoad.failed')}</h1>
    <p className="text-sm text-muted-foreground">{t('pageLoad.explanation')}</p>
    <Button type="button" onClick={onReload} className="h-auto min-h-11 max-w-full whitespace-normal">{t('pageLoad.reload')}</Button>
  </section></div>;
}

class PageErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode; resetKey?: string }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidUpdate(previous: Readonly<{ resetKey?: string }>) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

export function RouteContentBoundary({ children, resetKey, onReload = () => window.location.reload() }: { children: ReactNode; resetKey?: string; onReload?: () => void }) {
  return <PageErrorBoundary resetKey={resetKey} fallback={<PageLoadFailure onReload={onReload} />}>
    <Suspense fallback={<PageLoading />}>{children}</Suspense>
  </PageErrorBoundary>;
}

export function RouteWorkspace() {
  const { pathname } = useLocation();
  // Reset failures without remounting a healthy editor when its draft receives an ID.
  return <RouteContentBoundary resetKey={pathname}><Outlet /></RouteContentBoundary>;
}
